import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Box,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import { ZyppLiteApi } from "../../../API/Zypp-Lite-Api/LiteApi";
import { RentalPlan, RentalPlanChangeRequest, FleetType } from "../../../@Types/DRO/ZyppLiteApiResponse/ApiResponse";

const CreatePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editPlan = location.state?.editPlan as RentalPlan | undefined;
  const isEditMode = !!editPlan;

  const [fleetTypesList, setFleetTypesList] = useState<FleetType[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const [formData, setFormData] = useState<RentalPlanChangeRequest>({
    planId: editPlan?.id || null,
    rentalPlanName: editPlan?.rentalPlanName || "",
    planType: editPlan?.planType || "",
    rentAmount: editPlan?.rentAmount || 0,
    fleetTypeId: editPlan?.fleetTypeId || 0,
    validityInDays: editPlan?.validityInDays || 30,
    validityInKm: editPlan?.validityInKm || 0,
    active: editPlan?.active ?? true,
  });

  useEffect(() => {
    // Fetch fleet types from rental plans API
    ZyppLiteApi.api?.getRentalPlans().then((response) => {
      if (response?.data?.fleetTypes) {
        const fleetTypes = response.data.fleetTypes;
        setFleetTypesList(fleetTypes);
        
        // Set fleetTypeId based on fleetName when editing
        if (isEditMode && editPlan?.fleetName && !formData.fleetTypeId) {
          const matchingFleet = fleetTypes.find(
            (fleet: FleetType) => fleet.fleetName === editPlan.fleetName
          );
          if (matchingFleet) {
            setFormData((prev) => ({
              ...prev,
              fleetTypeId: matchingFleet.id,
            }));
          }
        }
      }
    });
  }, [editPlan?.fleetName, formData.fleetTypeId, isEditMode]);

  const handleBackToDashboard = () => {
    navigate("/evplans");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "rentAmount" || name === "validityInDays" || name === "validityInKm" || name === "fleetTypeId"
        ? Number(value) || 0
        : value,
    }));
  };

  const getSelectedFleetName = () => {
    const fleet = fleetTypesList.find((f) => f.id === formData.fleetTypeId);
    return fleet?.fleetName || "Zypp Nyx";
  };

  const calculatePerDay = () => {
    if (formData.validityInDays > 0 && formData.rentAmount > 0) {
      return Math.round(formData.rentAmount / formData.validityInDays);
    }
    return 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fleetTypeId) {
      setSnackbarMessage("Please select a Fleet Type");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    if (!formData.rentAmount || formData.rentAmount <= 0) {
      setSnackbarMessage("Please enter a valid Plan Amount");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: RentalPlanChangeRequest = {
        ...formData,
        planId: isEditMode ? editPlan?.id : null,
      };

      const response = await ZyppLiteApi.api.createOrUpdateRentalPlan(payload);

      if (response.code === 200 || response.message?.toLowerCase().includes("success")) {
        setSnackbarMessage(isEditMode ? "Plan updated successfully" : "Plan created successfully");
        setSnackbarSeverity("success");
        setShowSnackbar(true);

        setTimeout(() => {
          navigate("/evplans");
        }, 2000);
      } else {
        setSnackbarMessage(response.message || "Failed to save plan");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      }
    } catch (error: any) {
      console.error("API error:", error);
      setSnackbarMessage(error.response?.data?.message || "Failed to save plan. Please try again.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex bg-white min-h-80 box-shadow p-6 rounded-xl mx-16 mt-10">
        <div className="w-full">
          {/* Header */}
          <div className="flex items-center mb-6">
            <IconButton onClick={handleBackToDashboard}>
              <ArrowBackIcon />
            </IconButton>
            <h2 className="text-2xl font-semibold text-black ml-2">
              {isEditMode ? "Edit Plan" : "Create Plan"}
            </h2>
          </div>

          <hr className="mb-6" />

          <Grid container spacing={4}>
            {/* Form Section */}
            <Grid item xs={12} md={6}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Plan Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Plan Name"
                      name="rentalPlanName"
                      value={formData.rentalPlanName}
                      onChange={handleChange}
                      variant="standard"
                      placeholder="Monthly"
                    />
                  </Grid>
                   <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Plan Type"
                      name="planType"
                      value={formData.planType}
                      onChange={handleChange}
                      variant="standard"
                      
                    />
                  </Grid>

                  {/* Fleet Type */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth variant="standard" required>
                      <InputLabel>Fleet Type *</InputLabel>
                      <Select
                        name="fleetTypeId"
                        value={formData.fleetTypeId || ""}
                        onChange={handleChange}
                      >
                        {fleetTypesList.map((fleet: FleetType) => (
                          <MenuItem key={fleet.id} value={fleet.id}>
                            {fleet.fleetName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* No. Of Days */}
                  <Grid item xs={12} sm={6}>
                   <TextField
  fullWidth
  label="No. Of Days"
  name="validityInDays"
  type="number"
  value={formData.validityInDays || ""}
  onChange={handleChange}
  variant="standard"
  placeholder="60"
  error={formData.validityInDays < 1}
  helperText={
    formData.validityInDays < 1
      ? "Value must be at least 1 day"
      : ""
  }
  inputProps={{
    min: 1
  }}
/>
                  </Grid>

                  {/* Plan Amount */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Plan Amount *"
                      name="rentAmount"
                      type="number"
                      value={formData.rentAmount || ""}
                      onChange={handleChange}
                      variant="standard"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                      placeholder="2,000"
                      error={formData.rentAmount < 1}
  helperText={
    formData.rentAmount < 1
      ? "Amount must be at greater than ₹1"
      : ""
  }
  inputProps={{
    min: 1
  }}
                    />
                  </Grid>

                  {/* Distance */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Distance"
                      name="validityInKm"
                      type="number"
                      value={formData.validityInKm || ""}
                      onChange={handleChange}
                      variant="standard"
                      placeholder="2800"
                       error={formData.validityInDays < 1}
  helperText={
    formData.validityInDays < 1
      ? "Distance must be at greater than 1KM"
      : ""
  }
  inputProps={{
    min: 1
  }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">KMs</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12} className="flex justify-center mt-4">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{
                        backgroundColor: "#00cd5b",
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 500,
                        marginTop: "46px",
                        px: 6,
                        py: 1,
                        "&:hover": {
                          backgroundColor: "#00b851",
                        },
                      }}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Details"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {/* Preview Card Section */}
            <Grid item xs={16} md={6}>
              <Typography variant="h6" className="text-gray-600 mb-4">
                Preview Plan
              </Typography>
              
              <Box
                sx={{
                  background: "linear-gradient(180deg, #027C63 0%, #171F21 94.97%)",
                  borderRadius: "16px",
                  padding: "20px",
                  marginLeft: "82px",
                  color: "#fff",
                  maxWidth: "350px",
                }}
              >
                {/* Top Section - Days and Amount */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {formData.validityInDays || 0} Days
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {formData.validityInKm || 0} KMs
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      ₹ {formData.rentAmount?.toLocaleString() || "0.00"}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      ₹ {calculatePerDay()} Per Day
                    </Typography>
                  </Box>
                </Box>

                {/* Fleet Info */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    padding: "12px",
                    mb: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/create-plan-scooter-img.svg"
                    alt="Scooter"
                    sx={{ width: "60px", height: "60px", mr: 2 }}
                  />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {getSelectedFleetName()} Battery Smart
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      EB1234567B (0-3 months)
                    </Typography>
                  </Box>
                </Box>

                {/* Stats */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    padding: "12px",
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      80
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      km/Charge
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Upto {Math.round((formData.validityInKm || 0) / (formData.validityInDays || 1))} km
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Distance
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CreatePlan;
